/**
 * Team buildings: each team keeps its signature silhouette, the shared kit
 * (buildingKit.ts) supplies foundations, doors, windows, roof hardware, site
 * furniture and the ruin language.
 *
 * Design reference: design_sheets/buildings.html
 */

import * as THREE from 'three';
import { TEAMS, REFERENCE, BUILDING_PERSON_SCALE } from '../constants';
import type { TeamPalette } from '../types';
import {
  KIT, KitRng, mat, matE, matGlass, box, cyl, sphere, torus, at, group,
  pad, doorway, windowPanel, roofUnit, pipeRun, vent, solarPanel, lightPole, signPost, crate, barrel, pallet, ladder, dishMast,
  scorch, rubble, exposedFrame, bentPost,
  type Detail, CLASSIC_DETAIL, FLOOR, windMount, pAt, pDoor, pWindow, pWindowRows, pFloorBands, pStairs, pLadder, pRail, pRoofRail, pVestibule,
  pBarrel, pCrate, pPallet, pBench, pSign, pSandbags, pBollard, pRopePosts, pLightPole, pSolar,
} from './buildingKit';

export const BUILDING_NAMES = [
  'Survey Outpost', 'Greenhouse Pod', 'Visitor Experience Pavilion', 'Field Office Module', 'Supply Depot',
  'Site Trailer', 'Analytics Station', 'Standard Outpost', 'Salvage Station', 'Watchtower',
];

// 63.7: windows round a dome of radius `R` (person units) on its floors, leaning with its curve, clear of the azimuths in `skip`
function domeWindows(t: TeamPalette, r: boolean, R: number, floors: number, skip: number[]): THREE.Group {
  const g = new THREE.Group();
  for (let f = 0; f < floors; f++) {
    const y = f * FLOOR + 1.45;
    if (y > R - 1.2) break;
    const rr = Math.sqrt(R * R - y * y), n = Math.max(3, Math.floor((Math.PI * 2 * rr) / 4.2)), tilt = Math.asin(y / R);
    for (let i = 0; i < n; i++) {
      const a = ((i + 0.5) / n) * Math.PI * 2;
      if (skip.some(s => Math.abs(Math.atan2(Math.sin(a - s), Math.cos(a - s))) < 0.45)) continue;
      g.add(at(group(at(pWindow(t, r), 0, y, rr + 0.02, -tilt)), 0, 0, 0, 0, a, 0));
    }
  }
  return g;
}
// 63.7: windows round a hexagonal frustum's ground floor (bottom radius `rb`, top `rt`, `h` tall: person units), a door's gap in the +x face
function hexWindows(t: TeamPalette, r: boolean, rb: number, rt: number, h: number): THREE.Group {
  const g = new THREE.Group(), y = 1.45, rad = rb + (rt - rb) * (y / h), apo = rad * Math.cos(Math.PI / 6);
  for (let k = 0; k < 6; k++) {
    const a = Math.PI / 6 + k * (Math.PI / 3);
    const row = pWindowRows(t, r, rad, 1, { pitch: 2.8, ...(k === 1 ? { doorX: 0 } : {}) });
    g.add(at(group(at(row, 0, 0, apo + 0.06)), 0, 0, 0, 0, a, 0));
  }
  return g;
}

// 1 — SIDEREAL CONSERVANCY: dome + dish array
function sidereal(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 1.0, 0.9, 0.05, r));
  const dome = at(new THREE.Mesh(new THREE.SphereGeometry(0.36, 24, 12, 0, Math.PI * 2, 0, Math.PI * 0.5), mat(t.primary)), 0, 0.06, 0);
  g.add(dome, at(torus(0.355, 0.016, mat(t.accent), 6, 28), 0, 0.075, 0, Math.PI / 2));             // base seam
  if (r) {
    dome.scale.set(0.95, 0.5, 0.95);
    g.add(at(box(0.22, 0.14, 0.1, mat(KIT.glassDark)), 0.12, 0.14, 0.26, 0, 0.5, 0.2));                // breach
    g.add(at(dishMast(t, true), 0.18, 0.06, 0.05), scorch(0.5), rubble(t, rng, 7, 0.45), at(exposedFrame(0.3, 0.2, 0.3), -0.15, 0.06, -0.1));
  } else {
    if (!P) g.add(at(windowPanel(t, false, 0.16, 0.06), 0, 0.3, 0.262, -0.73));                                 // porthole strip, tangent to the dome
    g.add(at(torus(0.09, 0.012, mat(t.accent), 6, 16), 0, 0.42, 0, Math.PI / 2), at(sphere(0.03, matE(t.visor, 0.5), 8, 6), 0, 0.43, 0)); // crown light
    g.add(at(dishMast(t, false, 0.46), 0.22, 0.06, -0.1));
    g.add(at(cyl(0.006, 0.006, 0.3, mat(t.detail), 4), -0.22, 0.3, 0.12, 0, 0, 0.1));                   // whip antenna
  }
  if (P) {   // 63.7: an entry porch through the dome's foot, steps off the plinth, windows round two floors
    g.add(pAt(d, 0, 0.06, 0.36, 0, pVestibule(t, r)), pAt(d, 0, 0, 0.45, 0, pStairs(0.062 * d.u)));
    if (!r) g.add(pAt(d, 0, 0.06, 0, 0, domeWindows(t, r, 0.36 * d.u, 2, [0, Math.atan2(0.22, -0.1)])));
    g.add(pAt(d, -0.3, 0.062, -0.3, 0.4, pCrate(t, REFERENCE.crate.large), at(pCrate(t), 0.9, 0, 0.1, 0, 0.3, 0), at(pBarrel(t.accent), -0.9, 0, 0.4)));
  } else g.add(at(doorway(t, r), 0, 0.06, 0.345));
  if (P) g.add(pAt(d, -0.38, 0.062, 0.25, 0, pSolar(t, r), at(pSolar(t, r), -2.3, 0, 0)), pAt(d, 0.42, 0.062, 0.3, 0, pLightPole(t, r)));   // 63.7: site furniture at its own size
  else g.add(at(solarPanel(t, r), -0.38, 0.06, 0.25), at(lightPole(t, r), 0.42, 0.06, 0.3));
  g.add(at(pipeRun(0.3), -0.2, 0.09, 0.26, 0, 0.4, 0));
  return g;
}

// 2 — CRATER & BLOOM: ribbed greenhouse
function craterBloom(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 0.9, 0.62, 0.05, r));
  const glass = matGlass(t.visor, r ? 0.3 : 0.55);
  const shell = at(new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.64, 16, 1, true, 0, Math.PI), glass), 0, 0.06, 0, 0, Math.PI / 2, Math.PI / 2);
  if (r) { shell.scale.y = 0.5; shell.position.z = 0.04; }   // collapsed arch (no tilt: an edge would sink into the ground)
  g.add(shell);
  for (let i = -2; i <= 2; i++) {
    const rib = at(torus(0.245, 0.01, mat(t.accent), 6, 16, Math.PI), 0, 0.06, i * 0.15);   // a hoop over the tunnel (its axis is z)
    if (r && (i === 1 || i === -2)) { rib.scale.y = 0.55; rib.position.x = 0.05; }   // crushed ribs
    g.add(rib);
  }
  for (const sz of [-1, 1]) g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.24, 16, 0, Math.PI), mat(t.primary)), 0, 0.06, sz * 0.325, 0, sz > 0 ? 0 : Math.PI));
  if (P) g.add(pAt(d, 0, 0.06, 0.33, 0, pDoor(t, r)), pAt(d, 0, 0, 0.335, 0, pStairs(0.06 * d.u)));   // 63.7: the end door, steps down to it
  else g.add(at(doorway(t, r, 0.12, 0.2), 0, 0.06, 0.33));
  if (r) {
    g.add(scorch(0.45), rubble(t, rng, 6, 0.4));
    for (let i = 0; i < 3; i++) g.add(at(box(0.12, 0.006, 0.09, glass), rng.range(-0.3, 0.3), 0.07, rng.range(0.3, 0.42), 0, rng.range(0, 3), 0.3));
    g.add(at(box(0.3, 0.06, 0.08, mat(t.detail)), 0.2, 0.09, 0.15, 0, 0.3, 0.5));                      // spilled planter
  } else {
    g.add(at(box(0.04, 0.016, 0.56, matE(t.visor, 0.5)), 0, 0.29, 0));                                   // grow light
    for (const x of [-0.14, 0.14]) {
      g.add(at(box(0.08, 0.06, 0.5, mat(t.detail)), x, 0.09, 0));                                         // planters
      for (let k = -2; k <= 2; k++) g.add(at(sphere(0.025, mat('#4e8a52'), 6, 5), x, 0.14, k * 0.1));
    }
  }
  if (P) {
    g.add(pAt(d, -0.38, 0.062, -0.18, 0, pBarrel(t.accent), at(pBarrel(t.accent), 0.7, 0, 0.3)), at(pipeRun(0.22), -0.3, 0.12, -0.1, 0, 0.3, 0));
    g.add(pAt(d, 0.38, 0.062, 0.2, -0.4, pSign(t, r)), pAt(d, 0.4, 0.062, -0.22, 0, pLightPole(t, r)));
  } else {
    g.add(at(barrel(t.accent), -0.38, 0.06, -0.18), at(pipeRun(0.22), -0.3, 0.12, -0.1, 0, 0.3, 0));
    g.add(at(signPost(t, r, 0.3), 0.4, 0.06, 0.2), at(lightPole(t, r, 0.36), 0.4, 0.06, -0.22));
  }
  return g;
}

// 3 — TERRAGENTLY: visitor pavilion
function terraGently(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 0.9, 0.7, 0.05, r));
  const body = at(box(0.42, 0.48, 0.32, mat(t.primary)), 0, 0.3, -0.05);
  if (r) { body.rotation.z = 0.18; body.position.x = 0.04; body.scale.y = 0.85; }
  g.add(body);
  const canopy = group(at(box(0.7, 0.03, 0.5, mat(t.accent)), 0, 0, 0), at(box(0.66, 0.012, 0.46, mat(t.detail)), 0, 0.02, 0));
  const postA = at(cyl(0.015, 0.015, 0.52, mat(KIT.darkMetal), 6), 0.3, 0.32, 0.2), postB = at(cyl(0.015, 0.015, 0.52, mat(KIT.darkMetal), 6), -0.3, 0.32, 0.2);
  if (r) { at(canopy, 0.1, 0.33, 0.12, 0.2, 0.1, 0.45); postA.rotation.x = 0.9; postA.position.set(0.3, 0.2, 0.32); }
  else at(canopy, 0, 0.6, 0.02);
  g.add(canopy, postA, postB);
  const screen = group(at(box(0.32, 0.26, 0.02, mat(t.accent)), 0, 0, 0), at(box(0.28, 0.22, 0.012, r ? mat(KIT.glassDark) : matE(t.visor, 0.45)), 0, 0, 0.012));
  if (r) screen.add(at(box(0.2, 0.006, 0.014, mat(KIT.lightMetal)), 0, 0.02, 0.013, 0, 0, 0.6));
  g.add(at(screen, 0, P ? 0.4 : 0.33, 0.115 - (r ? 0.02 : 0)));   // 63.7: at person scale the display sits on the upper floor, over the door
  if (!r) g.add(at(torus(0.07, 0.012, mat(t.detail), 4, 12, Math.PI * 0.7), 0, 0.5, 0.12), at(box(0.04, 0.03, 0.03, matE(t.visor, 0.6)), 0, 0.58, 0.26)); // swoosh + spotlight
  if (P) {   // 63.7: the pavilion's two floors: a door under the display, windows round the sides and back, the slab edges, steps off the plinth
    g.add(pAt(d, 0.38, 0.062, -0.25, 0, pSign(t, r)), pAt(d, -0.3, 0.062, 0.26, r ? 0.3 : 0, pBench()), pAt(d, -0.22, 0.062, 0.22, Math.PI / 2, pRopePosts(t)));
    body.add(pAt(d, 0, -0.24, 0.161, 0, pDoor(t, r)), pAt(d, 0, -0.24, 0, 0, pFloorBands(t, 0.42 * d.u, 0.32 * d.u, 2)));
    for (const s of [-1, 1]) body.add(pAt(d, s * 0.211, -0.24, 0, s * Math.PI / 2, pWindowRows(t, r, 0.32 * d.u, 2)));
    body.add(pAt(d, 0, -0.24, -0.161, Math.PI, pWindowRows(t, r, 0.42 * d.u, 2)));
    g.add(pAt(d, 0, 0, 0.35, 0, pStairs(0.062 * d.u)));
  } else {
    g.add(at(signPost(t, r, 0.4), 0.38, 0.06, -0.25));
    const bench = group(at(box(0.22, 0.02, 0.07, mat('#9a8460')), 0, 0.08, 0), at(box(0.02, 0.07, 0.07, mat(KIT.darkMetal)), -0.09, 0.04, 0), at(box(0.02, 0.07, 0.07, mat(KIT.darkMetal)), 0.09, 0.04, 0));
    if (r) at(bench, -0.3, 0.06, 0.26, Math.PI, 0.3, 0); else at(bench, -0.3, 0.06, 0.26);
    g.add(bench);
    for (const x of [-0.12, 0.12]) g.add(at(cyl(0.01, 0.01, 0.12, mat(KIT.lightMetal), 6), x, 0.12, 0.33));   // rope posts
    g.add(at(box(0.24, 0.006, 0.006, mat(t.detail)), 0, 0.17, 0.33));
  }
  g.add(at(box(0.1, 0.07, 0.1, mat(t.detail)), -0.36, 0.095, -0.05), at(sphere(0.045, mat('#4e8a52'), 8, 6), -0.36, 0.16, -0.05));          // planter
  if (r) g.add(scorch(0.42), rubble(t, rng, 6, 0.4));
  return g;
}

// 4 — GREENORBIT: field office module
function greenOrbit(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 0.9, 0.7, 0.05, r));
  const body = at(box(0.54, 0.34, 0.4, mat(t.primary)), 0, 0.22, 0);
  if (r) body.rotation.z = 0.12;
  g.add(body);
  for (const sx of [-1, 1]) g.add(at(box(0.02, 0.34, 0.42, mat(t.accent)), sx * 0.27, 0.22, 0));          // end panels
  g.add(at(box(0.56, 0.02, 0.42, mat(t.accent)), 0, 0.4, 0));                                             // roof trim
  if (P) {   // 63.7: the module's two floors: the door at its end, windows front and back, the slab edges, steps off the plinth
    body.add(pAt(d, 0.281, -0.17, 0, Math.PI / 2, pDoor(t, r), pWindowRows(t, r, 0.4 * d.u, 2, { from: 1 })));
    body.add(pAt(d, 0, -0.17, 0.201, 0, pWindowRows(t, r, 0.54 * d.u, 2)), pAt(d, 0, -0.17, -0.201, Math.PI, pWindowRows(t, r, 0.54 * d.u, 2, { gapX: 0 })));
    body.add(pAt(d, 0, -0.17, 0, 0, pFloorBands(t, 0.54 * d.u, 0.4 * d.u, 2)));
    g.add(pAt(d, 0.45, 0, 0, Math.PI / 2, pStairs(0.062 * d.u)), pAt(d, -0.3, 0.062, -0.28, 0, pBarrel(t.accent), at(pBarrel(t.accent), 0.7, 0, 0.1), at(pCrate(t), -0.2, 0, 0.9)));
  } else {
    g.add(at(windowPanel(t, r, 0.36, 0.08), 0, 0.3, 0.21));
    g.add(at(doorway(t, r, 0.12, 0.2), 0.3, 0.05, 0.0, 0, Math.PI / 2, 0));
  }
  g.add(at(cyl(0.06, 0.06, 0.08, mat(t.accent), 8), -0.3, 0.24, 0, 0, 0, Math.PI / 2), at(box(0.14, 0.2, 0.26, mat(t.detail)), -0.4, 0.15, 0)); // connector + stub module
  if (r) {
    g.add(at(roofUnit(t), -0.3, 0.05, 0.3, 0, 0.5, 0.3), scorch(0.45), rubble(t, rng, 7, 0.42), at(exposedFrame(0.2, 0.18, 0.2), 0.15, 0.41, -0.1));
  } else {
    g.add(at(roofUnit(t), -0.12, 0.41, 0), at(vent(), 0.12, 0.41, -0.1), at(pipeRun(0.2), 0.0, 0.46, 0.1));
    g.add(at(cyl(0.008, 0.008, 0.16, mat(t.detail), 4), 0.2, 0.5, 0.1), P ? pAt(d, 0, 0.05, -0.211, Math.PI, pLadder(0.36 * d.u)) : at(ladder(0.3), 0.0, 0.08, -0.215));
    if (P) g.add(pAt(d, 0, 0.41, 0, 0, pRoofRail(0.54 * d.u, 0.4 * d.u)));
  }
  if (P) g.add(pAt(d, 0.4, 0.062, 0.28, 0, pLightPole(t, r)), pAt(d, -0.36, 0.062, 0.28, 0, pSolar(t, r), at(pSolar(t, r), 2.3, 0, 0)));
  else g.add(at(lightPole(t, r), 0.4, 0.05, 0.28), at(solarPanel(t, r), -0.36, 0.05, 0.28));
  return g;
}

// 5 — BIOSPHERE DIRECT: supply depot
function bioSphere(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 1.0, 0.7, 0.05, r));
  const cont = group(at(box(0.6, 0.3, 0.3, mat(t.primary)), 0, 0.15, 0));
  for (let i = -2; i <= 2; i++) cont.add(at(box(0.02, 0.308, 0.31, mat(t.accent)), i * 0.12, 0.15, 0));   // a hair taller than the body: no coplanar top
  for (const sx of [-1, 1]) for (const sy of [0.03, 0.27]) for (const sz of [-1, 1]) cont.add(at(box(0.03, 0.03, 0.03, mat(KIT.darkMetal)), sx * 0.3, sy, sz * 0.15)); // corner castings
  cont.add(at(box(0.1, 0.05, 0.004, mat(t.detail)), 0.15, 0.2, 0.152), at(box(0.06, 0.02, 0.005, mat(t.visor)), 0.15, 0.2, 0.153));     // label plate
  const door = group(at(box(0.012, 0.24, 0.24, mat(t.detail)), 0, 0.12, 0), at(cyl(0.008, 0.008, 0.2, mat(KIT.lightMetal), 6), 0.01, 0.12, -0.05), at(cyl(0.008, 0.008, 0.2, mat(KIT.lightMetal), 6), 0.01, 0.12, 0.05));
  if (r) { cont.rotation.z = 0.1; cont.rotation.y = 0.08; at(door, 0.44, 0.03, 0.16, 0.2, 1.1, 0); } else at(door, 0.306, 0.0, 0);
  g.add(at(cont, 0, 0.05, 0), door);
  const padTop = group(at(cyl(0.11, 0.11, 0.015, mat(t.detail), 16), 0, 0, 0), at(torus(0.08, 0.006, mat(t.visor), 4, 20), 0, 0.01, 0, Math.PI / 2), at(sphere(0.012, matE(t.visor, 0.6), 6, 6), 0.09, 0.015, 0));
  if (r) at(padTop, 0.1, 0.3, 0.08, 0.5, 0, 0.3); else at(padTop, 0, 0.355, 0);
  g.add(padTop);
  const stack = P ? pAt(d, 0, 0, 0, 0, pCrate(t, REFERENCE.crate.large), at(pCrate(t), 0, REFERENCE.crate.large, 0), at(pCrate(t, REFERENCE.crate.small), 0.9, 0, 0.1))
    : group(at(crate(t), 0, 0, 0), at(crate(t), 0.0, 0.12, 0), at(crate(t, 0.1), 0.14, 0, 0.02));
  if (r) { at(stack, -0.42, 0.05, 0.14, 0, 0.5, 0); stack.children[1].position.set(0.2, 0, 0.14); stack.children[1].rotation.y = 0.7; } else at(stack, -0.42, 0.05, 0.14);
  if (P) {   // 63.7: a container's own size; steps up to its cargo doors, a person door in its side, a ladder to the drone pad on its roof
    g.add(stack, pAt(d, -0.38, 0.062, -0.2, 0, pPallet(), at(pBarrel(t.accent), 0, 0.14, 0)), pAt(d, 0.4, 0.062, -0.22, 0, pBarrel(t.accent), at(pBarrel(t.accent), 0.65, 0, 0.2)));
    g.add(pAt(d, 0.5, 0, 0, Math.PI / 2, pStairs(0.062 * d.u, 2.2)));
    cont.add(pAt(d, 0.12, 0, 0.151, 0, pDoor(t, r)));   // a person door cut in the container's side, as site containers have
    if (!r) g.add(pAt(d, -0.2, 0.05, 0.151, 0, pLadder(0.3 * d.u)));
  } else g.add(stack, at(pallet(), -0.38, 0.05, -0.2), at(barrel(t.accent, 0.05, 0.14), 0.4, 0.05, -0.22));
  if (r) g.add(scorch(0.48), rubble(t, rng, 6, 0.45));
  else g.add(P ? pAt(d, 0.44, 0.062, 0.28, 0, pLightPole(t, false)) : at(lightPole(t, false), 0.44, 0.05, 0.28));
  return g;
}

// 6 — ASTRATURF: site trailer
function astraTurf(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = new THREE.Group();
  for (const z of [-0.16, 0.16]) g.add(at(box(0.7, 0.04, 0.06, mat(t.accent)), 0, 0.02, z));
  const body = group(at(box(0.6, 0.28, 0.35, mat(t.primary)), 0, 0.14, 0), at(box(0.64, 0.03, 0.38, mat(t.accent)), 0, 0.31, 0, 0.04), at(box(0.62, 0.02, 0.02, mat(t.detail)), 0, 0.05, 0.18));
  if (P) body.add(pAt(d, 0, 0, 0.181, 0, pWindowRows(t, r, 0.6 * d.u, 1, { pitch: 2.0 })), pAt(d, 0.301, 0, 0.04, Math.PI / 2, pDoor(t, r)), pAt(d, 0.302, -0.04, 0.04, Math.PI / 2, pStairs(0.04 * d.u, 1.2)));   // 63.7
  else {
    body.add(at(windowPanel(t, r, 0.14, 0.08), 0.08, 0.2, 0.18), at(windowPanel(t, r, 0.14, 0.08), -0.14, 0.2, 0.18));
    body.add(at(doorway(t, r, 0.12, 0.2), 0.31, 0.0, 0.04, 0, Math.PI / 2, 0));
  }
  if (r) { at(body, 0.06, 0.0, 0.03, 0.08, 0.1, 0.14); } else at(body, 0, 0.04, 0);
  g.add(body);
  g.add(at(cyl(0.02, 0.02, 0.16, mat(t.accent), 6), -0.38, 0.06, 0, 0, 0, Math.PI / 2), at(sphere(0.025, mat(KIT.darkMetal), 8, 6), -0.46, 0.06, 0));   // hitch
  const gen = group(at(box(0.16, 0.12, 0.12, mat(t.detail)), 0, 0.06, 0), at(cyl(0.012, 0.012, 0.1, mat(KIT.darkMetal), 6), 0.05, 0.16, -0.03), at(box(0.1, 0.008, 0.012, mat(KIT.darkMetal)), 0, 0.06, 0.062));
  g.add(at(gen, -0.4, 0.0, 0.26));
  if (r) {
    g.add(scorch(0.45), rubble(t, rng, 7, 0.42), at(bentPost(0.3, t.detail), 0.3, 0, 0.3));
    g.add(at(box(0.3, 0.012, 0.2, mat(t.accent)), 0.2, 0.33, 0.1, 0.3, 0.2, 0.4));                       // peeled roof sheet
  } else {
    g.add(at(roofUnit(t, 0.1), -0.15, 0.31, -0.08), ...(P ? [] : [at(cyl(0.008, 0.008, 0.35, mat(t.detail), 4), 0.2, 0.2, 0.22, -0.1, 0, 0.15)]));   // (at person scale the shovel would be 3 tall: left out)   // AC + shovel
    const barrow = group(at(box(0.14, 0.06, 0.1, mat(t.accent)), 0, 0.08, 0), at(cyl(0.035, 0.035, 0.02, mat(KIT.darkMetal), 10), 0.08, 0.035, 0, Math.PI / 2), at(cyl(0.006, 0.006, 0.18, mat(KIT.lightMetal), 4), -0.1, 0.07, 0.03, 0, 0, 1.2));
    g.add(at(barrow, 0.42, 0.0, -0.22, 0, 0.6, 0), P ? pAt(d, 0.42, 0, 0.26, 0, pLightPole(t, false, 4.5)) : at(lightPole(t, false, 0.38), 0.42, 0.0, 0.26));
  }
  return g;
}

// 7 — GENTLE FUTURES: analytics station
function gentleFutures(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 0.8, 0.8, 0.05, r));
  const lower = at(cyl(0.24, 0.26, 0.26, mat(t.primary), 6), 0, 0.18, 0);
  const upper = at(cyl(0.2, 0.24, 0.2, mat(t.primary), 6), 0, 0.41, 0);
  if (r) { at(upper, 0.08, 0.36, 0.05, 0.15, 0.5, 0.3); }
  g.add(lower, upper);
  const band = at(new THREE.Mesh(new THREE.CylinderGeometry(0.265, 0.265, 0.08, 6, 1, true), r ? mat(KIT.glassDark) : (() => { const m = matE(t.visor, 0.45); m.side = THREE.DoubleSide; return m; })()), 0, 0.31, 0);
  g.add(band, at(torus(0.27, 0.01, mat(t.accent), 6, 6), 0, 0.355, 0, Math.PI / 2), at(torus(0.27, 0.01, mat(t.accent), 6, 6), 0, 0.265, 0, Math.PI / 2));
  if (P) g.add(pAt(d, 0, 0.05, 0, 0, hexWindows(t, r, 0.26 * d.u, 0.24 * d.u, 0.26 * d.u)), pAt(d, 0.26 * Math.cos(Math.PI / 6) + 0.004, 0.05, 0, Math.PI / 2, pDoor(t, r)), pAt(d, 0.4, 0, 0, Math.PI / 2, pStairs(0.062 * d.u)));   // 63.7: a door in the +x face, windows round the ground floor, steps off the plinth
  const dish = group(at(cyl(0.03, 0.13, 0.05, mat(t.detail), 8), 0, 0, 0), at(cyl(0.012, 0.012, 0.06, mat(KIT.darkMetal), 6), 0, -0.05, 0));
  if (r) at(dish, 0.26, 0.12, 0.18, 0.9, 0, 0.4); else at(dish, 0, 0.56, 0, -0.3);
  g.add(dish);
  const spike = at(cyl(0.005, 0.015, 0.22, mat(t.accent), 4), 0, 0.64, 0);
  if (r) at(spike, 0.14, 0.4, 0.08, 0, 0, 1.0);
  g.add(spike);
  for (let i = 0; i < 3; i++) {
    const rack = group(at(box(0.08, 0.14, 0.08, mat(t.detail)), 0, 0.07, 0));
    if (P) rack.scale.setScalar(0.6);   // 63.7: equipment cabinets a person's size (1.9 tall)
    for (let k = 0; k < 3; k++) rack.add(at(box(0.012, 0.012, 0.004, r ? mat(KIT.glassDark) : matE(t.visor)), -0.02 + k * 0.02, 0.1, 0.042));   // LEDs (one glow bucket)
    if (r && i !== 1) at(rack, -0.3 + i * 0.13, 0.05, 0.3, Math.PI / 2, 0.3, 0); else at(rack, -0.3 + i * 0.13, 0.05, 0.3);
    g.add(rack);
  }
  g.add(at(pipeRun(0.3, 0.01), -0.1, 0.07, 0.24, 0, 0, 0));
  if (r) g.add(scorch(0.42), rubble(t, rng, 6, 0.4));
  else g.add(P ? pAt(d, 0.36, 0.062, -0.3, 0, pLightPole(t, false)) : at(lightPole(t, false, 0.4), 0.36, 0.05, -0.3));
  return g;
}

// 8 — PLANETCARE: standard outpost (regulation everything)
function planetCare(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(pad(t, 0.8, 0.7, 0.05, r));
  const body = at(box(0.5, 0.34, 0.4, mat(t.primary)), 0, 0.22, 0);
  if (r) body.rotation.y = 0.1;
  g.add(body);
  const roof = group(at(box(0.52, 0.02, 0.42, mat(t.accent)), 0, 0, 0));
  for (const sx of [-1, 1]) roof.add(at(box(0.02, 0.04, 0.42, mat(t.accent)), sx * 0.25, 0.03, 0));
  for (const sz of [-1, 1]) roof.add(at(box(0.52, 0.04, 0.02, mat(t.accent)), 0, 0.03, sz * 0.2));
  if (r) at(roof, 0.12, 0.33, 0.05, 0.1, 0.1, 0.35); else at(roof, 0, 0.4, 0);
  g.add(roof);
  if (P) {   // 63.7: two floors by regulation: the door, windows all round, the slab edges, a plaque by the door, a ladder up the back, steps off the plinth
    body.add(pAt(d, 0, -0.17, 0.201, 0, pDoor(t, r), pWindowRows(t, r, 0.5 * d.u, 2, { doorX: 0 }), at(box(0.7, 0.45, 0.04, mat(t.detail)), -1.2, 1.5, 0.02), at(box(0.5, 0.12, 0.05, mat(t.visor)), -1.2, 1.6, 0.03)));
    body.add(pAt(d, 0, -0.17, -0.201, Math.PI, pWindowRows(t, r, 0.5 * d.u, 2, { gapX: 0 })), pAt(d, 0, -0.17, 0, 0, pFloorBands(t, 0.5 * d.u, 0.4 * d.u, 2)));
    for (const s of [-1, 1]) body.add(pAt(d, s * 0.251, -0.17, 0, s * Math.PI / 2, pWindowRows(t, r, 0.4 * d.u, 2)));
    g.add(pAt(d, 0, 0, 0.35, 0, pStairs(0.062 * d.u)));
    if (!r) g.add(pAt(d, 0, 0.05, -0.201, Math.PI, pLadder(0.36 * d.u)));
  } else {
    g.add(at(doorway(t, r, 0.14, 0.22), 0, 0.05, 0.205));
    g.add(at(windowPanel(t, r, 0.1, 0.1), -0.16, 0.3, 0.205), at(windowPanel(t, r, 0.1, 0.1), 0.16, 0.3, 0.205));
    g.add(at(box(0.1, 0.06, 0.006, mat(t.detail)), -0.16, 0.14, 0.205), at(box(0.07, 0.02, 0.007, mat(t.visor)), -0.16, 0.15, 0.206));   // regulation plaque
  }
  g.add(at(cyl(0.012, 0.012, 0.3, mat(KIT.lightMetal), 6), 0.26, 0.2, 0.15));                                                           // drainpipe
  g.add(at(box(0.08, 0.1, 0.05, mat(t.accent)), 0.3, 0.1, -0.1));                                                                        // utility box
  if (r) g.add(scorch(0.42), rubble(t, rng, 6, 0.38), at(exposedFrame(0.16, 0.14, 0.16), -0.15, 0.39, -0.1));
  else g.add(at(vent(0.08), -0.15, 0.41, -0.1), at(cyl(0.008, 0.008, 0.16, mat(t.detail), 4), 0.18, 0.49, -0.12), P ? pAt(d, 0.36, 0.062, 0.28, 0, pLightPole(t, false)) : at(lightPole(t, false, 0.36), 0.36, 0.05, 0.28));
  return g;
}

// 9 — ORBITAL RECLAMATION: salvage station
function orbitalReclam(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = new THREE.Group();
  const pod = group(
    at(cyl(0.22, 0.22, 0.6, mat(t.primary), 12), 0, 0, 0, 0, 0, Math.PI / 2),
    at(new THREE.Mesh(new THREE.CircleGeometry(0.22, 12), mat(t.accent)), -0.301, 0, 0, 0, -Math.PI / 2, 0),
    at(torus(0.1, 0.012, mat(KIT.darkMetal), 6, 16), 0.302, 0, 0, 0, Math.PI / 2, 0), at(new THREE.Mesh(new THREE.CircleGeometry(0.1, 12), mat(KIT.glassDark)), 0.303, 0, 0, 0, Math.PI / 2, 0),   // hatch
    at(box(0.2, 0.012, 0.15, mat(t.detail)), 0.05, 0.21, 0.02, -0.3), at(box(0.14, 0.012, 0.12, mat(KIT.rubbleDark)), -0.15, 0.2, -0.05, 0.4),  // mismatched patches
  );
  const struts: THREE.Mesh[] = [];
  for (const [x, z] of [[-0.18, 0.16], [0.18, -0.16], [0.12, 0.17]]) struts.push(at(cyl(0.018, 0.022, 0.2, mat(t.accent), 6), x, 0.1, z, z > 0 ? -0.25 : 0.25, 0, x > 0 ? -0.2 : 0.2));
  if (r) { at(pod, 0.04, 0.3, 0.0, 0.2, 0.1, 0.1); for (const s of struts) { s.rotation.z += 1.2; s.position.y = 0.03; } }
  else at(pod, 0, 0.36, 0);
  g.add(pod, ...struts);
  if (P) {   // 63.7: portholes down the hull; a stoop door in the end hatch, a landing at it and steps down from it
    for (const s of [-1, 1]) pod.add(pAt(d, 0, -1.45 / d.u, s * 0.221, s > 0 ? 0 : Math.PI, pWindowRows(t, r, 0.6 * d.u - 1.0, 1, { pitch: 2.0 })));
    pod.add(pAt(d, 0.304, -0.08, 0, Math.PI / 2, pDoor(t, r, 'stoopDoor')));   // the end hatch's door: ducked through
    if (!r) g.add(pAt(d, 0.302, 0, 0, 0, at(box(1.8, 0.15, 2.0, mat(KIT.darkMetal)), 0.9, 0.36 * d.u - 1.0 - 0.075, 0), at(pStairs(0.36 * d.u - 1.0, 1.2), 0.9, 0, 1.0),
      at(pRail(2.0), 1.75, 0.36 * d.u - 1.0, 0, 0, Math.PI / 2, 0), at(box(0.12, 0.36 * d.u - 1.0, 0.12, mat(KIT.darkMetal)), 1.6, (0.36 * d.u - 1.0) / 2, -0.8)));
  }
  const awning = group(at(box(0.32, 0.012, 0.26, mat(t.detail)), 0, 0.3, 0, 0.3), at(cyl(0.01, 0.01, 0.3, mat(KIT.darkMetal), 6), 0.14, 0.15, 0.12), at(cyl(0.01, 0.01, 0.3, mat(KIT.darkMetal), 6), -0.14, 0.15, 0.12));
  if (r) at(awning, 0.1, 0.12, 0.35, 0.2, 0.3, 0.6); else at(awning, 0.1, 0.0, 0.28);
  g.add(awning);
  const pile = group(at(cyl(0.02, 0.02, 0.26, mat(KIT.lightMetal), 6), 0, 0.02, 0, 0, 0.3, Math.PI / 2), at(torus(0.07, 0.015, mat(KIT.darkMetal), 6, 12, Math.PI), 0.1, 0.03, 0.05, 0, 0.6, 0), at(box(0.14, 0.01, 0.1, mat(t.accent)), -0.05, 0.05, 0.06, 0.2, 0.4, 0.1));
  g.add(at(pile, -0.42, 0, -0.2));
  const crane = group(at(box(0.04, 0.3, 0.04, mat(t.accent)), 0, 0.15, 0), at(box(0.3, 0.03, 0.03, mat(t.accent)), 0.12, 0.3, 0), at(cyl(0.004, 0.004, 0.14, mat(KIT.darkMetal), 4), 0.26, 0.22, 0), at(box(0.04, 0.03, 0.04, mat(KIT.darkMetal)), 0.26, 0.14, 0));
  if (r) at(crane, 0.38, 0, -0.1, 0, 0.4, 1.1); else at(crane, 0.38, 0, -0.1, 0, 0.4, 0);
  if (P) g.add(crane, pAt(d, -0.2, 0, 0.34, 0, pBarrel(KIT.rubbleDark), at(pBarrel(KIT.rubbleDark), 0.65, 0, 0.15)));
  else g.add(crane, at(barrel(KIT.rubbleDark, 0.05, 0.13), 0.42, 0, 0.2));
  if (r) g.add(scorch(0.45), rubble(t, rng, 9, 0.45));
  else g.add(P ? pAt(d, -0.4, 0, 0.26, 0, pLightPole(t, false, 4.5)) : at(lightPole(t, false, 0.34), -0.4, 0, 0.26));
  return g;
}

// 10 — ECOSHIELD: watchtower
function ecoShield(t: TeamPalette, r: boolean, rng: KitRng, d: Detail): THREE.Group {
  const P = d.u > 0;
  const g = group(at(box(0.5, 0.2, 0.5, mat(t.accent)), 0, 0.1, 0), at(box(0.56, 0.03, 0.56, mat(KIT.concrete)), 0, 0.015, 0));
  const tower = group(at(box(0.34, 0.44, 0.34, mat(t.primary)), 0, 0.22, 0));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) tower.add(at(box(0.04, 0.46, 0.04, mat(t.accent)), sx * 0.17, 0.23, sz * 0.17));   // pilasters
  tower.add(at(windowPanel(t, r, 0.2, 0.04), 0, 0.33, 0.175), at(box(0.1, 0.1, 0.012, mat(t.detail)), 0, 0.14, 0.176), at(box(0.06, 0.02, 0.014, mat(t.visor)), 0, 0.15, 0.177)); // slit + emblem
  if (P) {   // 63.7: the tower's two floors (windows on three faces, the emblem's face bare), a ladder up its back
    for (const s of [-1, 1]) tower.add(pAt(d, s * 0.171, 0, 0, s * Math.PI / 2, pWindowRows(t, r, 0.34 * d.u, 1)));
    tower.add(pAt(d, 0, 0, -0.171, Math.PI, pWindowRows(t, r, 0.34 * d.u, 1), at(pLadder(0.44 * d.u), 0, 0, 0)), pAt(d, 0, 0, 0, 0, pFloorBands(t, 0.34 * d.u, 0.34 * d.u, 2)));
  }
  if (r) { tower.scale.y = 0.62; tower.rotation.z = 0.08; }
  g.add(at(tower, 0, 0.2, 0));
  const roof = group(at(box(0.44, 0.04, 0.44, mat(t.detail)), 0, 0, 0));
  for (const sx of [-1, 1]) roof.add(at(box(0.02, 0.05, 0.44, mat(t.accent)), sx * 0.21, 0.04, 0)); for (const sz of [-1, 1]) roof.add(at(box(0.44, 0.05, 0.02, mat(t.accent)), 0, 0.04, sz * 0.21));
  const search = group(at(cyl(0.035, 0.03, 0.08, mat(KIT.darkMetal), 10), 0, 0, 0, Math.PI / 2 - 0.6), at(new THREE.Mesh(new THREE.CircleGeometry(0.034, 10), r ? mat(KIT.glassDark) : matE(t.visor, 0.9)), 0, 0.023, 0.034, -0.6));
  if (r) { at(roof, 0.16, 0.42, 0.1, 0.2, 0.1, 0.5); at(search, -0.3, 0.05, 0.25, 0.3, 0.8, 1.2); }
  else { at(roof, 0, 0.66, 0); at(search, 0.12, 0.72, 0.12, 0, 0.6, 0); }
  g.add(roof, search);
  const mast = at(cyl(0.012, 0.012, 0.26, mat(t.detail), 4), -0.12, 0.8, -0.12);
  if (r) at(mast, 0.1, 0.4, -0.15, 0, 0, 0.7);
  g.add(mast);
  if (P) {   // 63.7: the base's floor (door, windows), stairs up its side to the roof round the tower, sandbag walls and bollards at their real size
    g.add(pAt(d, 0, 0.03, 0.251, 0, pDoor(t, r), pWindowRows(t, r, 0.5 * d.u, 1, { pitch: 3.2, doorX: 0 })), pAt(d, 0, 0, 0.28, 0, pStairs(0.03 * d.u)));
    g.add(pAt(d, -0.251, 0.03, 0, -Math.PI / 2, pWindowRows(t, r, 0.5 * d.u, 1, { pitch: 3.2 })), pAt(d, 0, 0.03, -0.251, Math.PI, pWindowRows(t, r, 0.5 * d.u, 1, { pitch: 3.2 })));
    g.add(pAt(d, 0.25 + 0.7 / d.u, 0, -0.1, 0, pStairs(0.2 * d.u, 1.3)));
    if (!r) g.add(pAt(d, 0, 0.2, 0, 0, at(pRail(0.5 * d.u), 0, 0, 0.5 * d.u / 2 - 0.1), at(pRail(0.5 * d.u), 0, 0, -0.5 * d.u / 2 + 0.1), at(pRail(0.5 * d.u), -0.5 * d.u / 2 + 0.1, 0, 0, 0, Math.PI / 2, 0)));
    for (const sx of [-1, 1]) g.add(pAt(d, sx * 0.2, 0, 0.37, 0, pSandbags(4, 2)), pAt(d, sx * 0.42, 0, 0.3, 0, pBollard(t)));
  } else {
    for (let i = 0; i < 10; i++) { const a = (i / 10) * Math.PI * 2; const sb = at(sphere(0.045, mat('#a89a78'), 8, 6), Math.cos(a) * 0.33, 0.03, Math.sin(a) * 0.33); sb.scale.set(1.3, 0.6, 1); g.add(sb); }   // sandbag ring
    for (const x of [-0.42, 0.42]) g.add(at(cyl(0.015, 0.015, 0.1, mat(t.detail), 6), x, 0.05, 0.3));                              // bollards
  }
  if (r) g.add(scorch(0.48), rubble(t, rng, 8, 0.42));
  return g;
}

const BUILDERS = [sidereal, craterBloom, terraGently, greenOrbit, bioSphere, astraTurf, gentleFutures, planetCare, orbitalReclam, ecoShield] as const;

/**
 * A team's structure. `scale` 'classic' is today's model (building-local units, planet-scaled by the caller); 'person'
 * (63.7, decision 135 B) is the same silhouette sized by function — BUILDING_PERSON_SCALE.team's `u` — with its doors,
 * windows, floors, stairs and furniture at REFERENCE, returned in person units (a person is 1.8).
 */
export function buildTeamStructure(teamIndex: number, ruined: boolean, scale: 'classic' | 'person' = 'classic'): THREE.Group {
  const idx = teamIndex % BUILDERS.length;
  const t = TEAMS[teamIndex % TEAMS.length];
  if (scale === 'classic') return withSock(BUILDERS[idx](t, ruined, new KitRng(idx * 7 + (ruined ? 3 : 1)), CLASSIC_DETAIL), ruined);
  const u = BUILDING_PERSON_SCALE.team[idx].u, g = withSock(BUILDERS[idx](t, ruined, new KitRng(idx * 7 + (ruined ? 3 : 1)), { u }), ruined);
  g.scale.setScalar(u);
  return group(g);
}

/** 67.36: an intact structure's windsock stands just off its pad's front-right corner (a ruin has none). */
function withSock(g: THREE.Group, ruined: boolean): THREE.Group {
  if (ruined) return g;
  g.updateMatrixWorld(true);
  const b = new THREE.Box3().setFromObject(g);
  g.add(windMount('sock', b.max.x + 0.03, 0, b.max.z + 0.03));
  return g;
}
